import * as React from 'react';
import { cloneDeep, filter, flow, forEach, get, isEmpty, map, mapValues, size } from 'lodash';
import { Field, FieldArray, formValueSelector, InjectedFormProps, reduxForm } from 'redux-form';
import { IMtaActions, IMtaFormNames, PageForm } from '../../../../../../../../services/mta/models';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../../../redux/IStore';
import { push } from 'react-router-redux';
import { bindActionCreators } from 'redux';
import { mtaActions, } from '../../../../../../../../services/mta/actions';
import { actions as brandingActions } from '../../../../../../../../services/branding/reducer';
import { Button, Col, Form, Grid, OverlayTrigger, Popover, Row } from 'react-bootstrap';
import MtaHeader from '../../../../../../../../components/MtaHeader';
import * as Commonmark from 'react-commonmark';
import { isRequired } from '../../../../../../../../util/validators';
import confirm from 'react-confirm2';
import { IBrandingActions, IBrandingState, II18nMessages } from '../../../../../../../../services/branding/models';
import { IDeclaration, IProduct, ISelectedTravellers, ITraveller } from '../../../../../../../../data/api/models';
import { IQuoteState } from '../../../../../../../../services/quote/models';
import MTATravellersMedical from './component/MTATravellersMedical';
import FieldFormControl from '../../../../../../../../components/FieldFormControl';
import { FaQuestionCircle } from 'react-icons/fa';
import mapMedicalQuestions from '../../../../../../../../helpers/medicalDeclaratonQuestions';
import { mentalHealthLinkRenderer } from '../../../../../../../../helpers/Quote';
import { SelectTraveller } from './component/SelectTraveller';
import { MTACardHeader } from '../Components/MTACardHeader';
import { MTAButtons } from '../../../MTAButtons';
import { MedicalInformation } from '../../../../../../../Quote/scenes/MedicalDeclaration/components/MedicalInformation';
import { mtaMedicalDeclarationQuestionAnswers } from '../../../../Helpers';
import { IVeriskState } from '../../../../../../../../services/verisk/models';
import { WarningMessages } from '../../../../../../../Quote/scenes/Verisk/scenes/WarningMessages';
import { stripTimezone, isoFormat } from '../../../../../../../../helpers/DateManipulation';
import { isValid, parse, format } from 'date-fns';

interface IMedicalDeclaration extends InjectedFormProps {
  i18n: II18nMessages;
  product: IProduct;
  pristine: boolean;
  quote: IQuoteState;
  submitting: boolean;
  mtaIsLoading: boolean;
  onBack: () => void;
  dispatch: any;
  traveller: ITraveller;
  salesChannel: string;
  medicalDeclaration: IDeclaration[];
  medicalDeclarationFromProduct: IDeclaration[];

  renderDefinition(name: string, useProvidedString?: string): any;

  actions: {
    mta: IMtaActions;
    branding: IBrandingActions;
    push(path: string);
  };
  branding: IBrandingState;
  synchronousError: any;
  metadata: any;
  mta: any;
  travellers: ITraveller[];
  formValues: any;
  screening: IVeriskState;
  selectedTravellers: ISelectedTravellers;
}

interface IMedicalDeclarationState {
  isScreening: boolean;
  isAnyModalOpen: boolean;
  unableToProvideQuote: boolean;
  showTravellerSelection: boolean;
  question: string;
}

class MTAMedicalDeclarationForm extends React.Component<IMedicalDeclaration, IMedicalDeclarationState> {

  public state = {
    isScreening: false,
    isAnyModalOpen: false,
    unableToProvideQuote: false,
    showTravellerSelection: false,
    question: null,
  };

  constructor(props) {
    super(props);
    const { change } = props;
    change('page', PageForm.MTA_MEDICAL_DECLARATION);
    change('changingMedical', true);
    window.scrollTo(0, 0);
  }

  private shouldShowScreeningSection = () => {
    const { medicalDeclaration } = this.props;

    if (!medicalDeclaration) {
      return false;
    }

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === undefined && question.question_type === 'boolean',
    );

    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));

    return unanswered.length === 0 && trueAnswers.length > 0;
  }

  private setIsScreening = (isScreening: boolean) => {
    this.setState({
      isScreening,
    });
  }

  private checkForErrorsBeforeSubmit = () => {
    const { synchronousError, actions, formValues, change } = this.props;
    if (synchronousError && !isEmpty(synchronousError)) {
      window.scrollTo(0, 0);
      return;
    }

    const adults = formValues.travellers.filter(({ type }) =>
      type === 'adult').map(this.mapTraveller('adult'));
    const children = formValues.travellers.filter(({ type }) =>
      type === 'child').map(this.mapTraveller('child'));

    Promise.all([
      Promise.resolve(change('data.attributes.metadata.adults', adults)),
      Promise.resolve(change('data.attributes.metadata.children', children)),
      actions.mta.calculateMtaPremiums(),
    ]).then(() => {
      const selectedScheme = actions.mta.getSelectedScheme();
      actions.mta.addEndorsements();
      actions.mta.handleMTASubmit().then(() => {
        setTimeout(() => {
          if (selectedScheme.errors && selectedScheme.errors.length > 0) {
            actions.push('/account/make-a-change/complete');
          } else {
            actions.push('/account/make-a-change/mta/summary');
          }
        }, 300);

      });
    });
  }

  private mapTraveller = (type) => (traveller: ITraveller, key: number) => {
    const data: any = {
      id: traveller.id,
      title: traveller.title,
      first_name: traveller.firstName,
      last_name: traveller.lastName,
      dob: this.dateToApi(traveller.dob),
      screening: traveller.screening,
      screening_id: traveller.screeningSessionId,
      conditions_covered: true,
      marketing_options: traveller.marketingOptions,
      metadata: traveller.metadata,
    };

    if (key === 0 && type === 'adult') {
      data.email = traveller.email;
      data.phone1 = traveller.phone;
      data.address = traveller.address;
    }

    return data;
  }

  private dateToApi = (date) => {
    if (!date) {
      return '';
    }

    const parsedDate = stripTimezone(parse(date, 'dd/MM/yyyy', new Date()));

    return isValid(parsedDate) ? format(parsedDate, isoFormat) : '';
  }

  private checkIfConditionWasDeclared = () => {
    const { travellers, medicalDeclaration } = this.props;

    const questions = filter(medicalDeclaration, (question) => question.type === 'question');
    const unanswered = filter(questions, (question) =>
      question.answer === undefined && question.question_type === 'boolean',
    );
    const trueAnswers = filter(questions, (question) => question.answer === true &&
      !get(question, 'should_not_screen', false));
    const travellerScreening = filter(travellers, (traveller) => traveller.screening);
    const travellersLength = travellers.length;
    const shouldScreen = unanswered.length === 0 && trueAnswers.length > 0;

    if (!shouldScreen) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length === travellersLength) {
      this.checkForErrorsBeforeSubmit();
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length === 0) {
      this.setState({ isAnyModalOpen: true });
      confirm('Finish Medical Screening', {
        description: (
          <span>
            <h4>Warning - You cannot continue without declaring a medical condition.</h4>
            Please click on 'Add Conditions' button to add your conditions.
            <br/>
          </span>
        ),
        done: () => {
          this.setState({ isAnyModalOpen: false });
          return;
        },
        close: () => {
          this.setState({ isAnyModalOpen: false });
          return;
        },
      });
    }

    if (shouldScreen && travellerScreening.length < travellersLength && travellerScreening.length > 0) {
      this.checkForErrorsBeforeSubmit();
    }
  }

  private renderDefinition = (product: IProduct) => (name: string, useProvidedString?: string) => {
    const definition = useProvidedString ? useProvidedString : product.metadata.definitions[name];
    let className;

    if (!definition) {
      return null;
    }

    const popover = (
      <Popover
        id={`popover-${name}`}
      >
        <Commonmark
          source={definition}
          renderers={{ link: mentalHealthLinkRenderer }}
        />
      </Popover>
    );

    className = (name === 'region' || name === 'destinations') ? 'info-btn no-margin' : 'info-btn';

    return (
      <OverlayTrigger
        trigger="click"
        rootClose={true}
        overlay={popover}
        placement="bottom"
        positionLeft={200}
        positionTop={50}
      >
        <FaQuestionCircle data-d={name}
                          className={className}/>
      </OverlayTrigger>
    );
  }

  public renderQuestions() {
    const { metadata, actions, change, medicalDeclarationFromProduct, salesChannel } = this.props;
    let { medicalDeclaration } = this.props
    const displayKeys = [];
    const product = actions.branding.getProduct();
    const aggregatorSource = get(metadata, 'aggregator.source', '');
    const isSourceCyti = !!(aggregatorSource && aggregatorSource === 'cyti');
    const isMSMorTSM = salesChannel === 'msm' || salesChannel === 'tsm' || salesChannel === 'msm-renewal' || salesChannel === 'tsm-renewal' || salesChannel === 'cyti-mtc' || salesChannel === 'cyti-mtc-renewal';

    if (isSourceCyti && isMSMorTSM && size(medicalDeclaration) <= 3) {
      if (!medicalDeclarationFromProduct) {
        // TODO if 'medicalDeclaration' from the state is only being used in this file then it is not needed.
        //   remove this block of "if", remove "medicalDeclarationFromProduct", and in "else" assign medical from product.
        change(
          'medicalDeclaration', // this is only being fed back into "medicalDeclarationFromProduct"; another piece of code was setting this, but it was never read.
          mapValues(cloneDeep(
              product.metadata.medical_declaration_questions),
            (declaration) => ({ ...declaration, answer: false }),
          ),
        );
      } else {
        medicalDeclaration = medicalDeclarationFromProduct; // = product.metadata.medical_declaration_questions
      }
    }

    // TODO: hack for old medical questions
    if (!isEmpty(medicalDeclaration)) {
      const mappedQuestions = mapMedicalQuestions(salesChannel, medicalDeclaration);
      change('data.attributes.metadata.medical_declaration_questions', mappedQuestions);
    }

    forEach(medicalDeclaration, (declaration: IDeclaration) => {
      const answer = declaration.answer;
      const questionType = declaration.question_type;

      if (questionType === 'boolean' && answer === 'true') {
        if (declaration.next_key) {
          displayKeys.push(declaration.next_key);
        }

        if (declaration.true_key) {
          displayKeys.push(declaration.true_key);
        }
      }

      if (questionType === 'boolean' && answer === 'false') {
        if (declaration.next_key) {
          displayKeys.push(declaration.next_key);
        }

        if (declaration.false_key) {
          displayKeys.push(declaration.false_key);
        }
      }
    });

    return (
      <div className="medical-questions-container">
        {map(medicalDeclaration, (question: any, key) => {
          let component;

          if (question.display === false && displayKeys.indexOf(key) === -1) {
            return null;
          }

          if (question.type === 'header') {
            component = this.renderMedicalHeader(question, key);
          }

          if (question.type === 'question') {
            component = this.renderMedicalQuestion(question, key);
          }

          return component;
        })}

      </div>
    );
  }

  private renderMedicalQuestion = (item, key) => {
    const { actions, travellers, selectedTravellers, change, formValues } = this.props;
    const product = actions.branding.getProduct();
    const renderDefinition = this.renderDefinition(product);
    const mtcChannels = ['mtc', 'mtc-renewal']
    const isMTCChannel = mtcChannels.includes(get(formValues, 'metadata.sales_channel', ''))
    let component = null;

    if (item.question_type === 'boolean') {
      component = [
        (
          <Col xs={6} key={1}>
            <Field
              name={`data.attributes.metadata.medical_declaration_questions.${key}.answer`}
              type="radio"
              format={null}
              value={true}
              complexValue={true}
              className="answer"
              component={FieldFormControl}
              validate={[isRequired]}
            >
              <div className="medical-btn yes">
                Yes
              </div>
            </Field>
          </Col>
        ),
        (
          <Col xs={6} key={2}>
            <Field
              name={`data.attributes.metadata.medical_declaration_questions.${key}.answer`}
              type="radio"
              format={null}
              value={false}
              complexValue={true}
              className="answer"
              component={FieldFormControl}
              validate={[isRequired]}
            >
              <div className="medical-btn no">
                No
              </div>
            </Field>
          </Col>
        ),
      ];
    }
    if (item.question_type === 'number') {
      component = (
        <div>
          <Field
            name={`medicalDeclaration[${key}].answer`}
            className="answer-number"
            component={FieldFormControl}
          />
        </div>
      );
    }

    const isTerminal = key === 'is-terminal' || key === (isMTCChannel ? '4' : '5');
    const isWaitingList = key === 'is-awaiting-medical-investigation-treatment' || key === (isMTCChannel ? '3' : '4');

    return (
      <div className="medical-question medical-section" key={key}>
        <Row className="dynamic-row">
          <Col sm={7}>
            <Commonmark source={item.content}/>
            <div className="remove-margins-from-children">
              {renderDefinition(item.true_key, item.help_text)}
            </div>
          </Col>
          <Col sm={5}>
            {component}
          </Col>
          <Col xs={12}>
            {(isTerminal || isWaitingList) &&
              <SelectTraveller
                travellers={travellers}
                selectedTravellers={selectedTravellers}
                change={change}
                formValues={formValues}
                isTerminal={isTerminal}
                isWaitingList={isWaitingList}
              />}
          </Col>
        </Row>
      </div>
    );
  }

  private renderMedicalHeader = (item, key) => {
    return (
      <div key={key}>
        <p>
          {item.content}
        </p>
      </div>
    );
  }

  private checkIfButtonIsDisabledOnCondition = () => {
    const { selectedTravellers, formValues } = this.props;

    const criticalAnswers = mtaMedicalDeclarationQuestionAnswers(formValues)

    if (criticalAnswers?.terminalAnswer && !selectedTravellers?.isTerminal?.length) {
      return true;
    }

    return criticalAnswers?.waitingListAnswer && !selectedTravellers?.waitingList?.length;

  }

  public render() {
    const {
      handleSubmit,
      submitting,
      i18n,
      change,
      metadata,
      mtaIsLoading,
      submitFailed,
      valid,
      mta,
      medicalDeclaration,
      screening,
    } = this.props;
    const policyNumber = mta.selectedPolicy.policyNumber || null;
    const { isAnyModalOpen, unableToProvideQuote } = this.state;

    const submitButton = (
      <Button
        bsStyle="primary"
        onClick={this.checkIfConditionWasDeclared}
        className="medium-width pull-right secondary-button"
        disabled={
          submitting ||
          (submitFailed && !valid || !valid || isAnyModalOpen || mtaIsLoading)
          || this.checkIfButtonIsDisabledOnCondition()
        }
      >
        Continue
      </Button>
    );

    if (!medicalDeclaration) {
      return false;
    }

    return (
      <>
        <MtaHeader title={`Make a Change - Update Cover Details ${policyNumber}`}/>
        <Form horizontal={true} onSubmit={handleSubmit} autoComplete="off">
          {isRequired && submitFailed && !valid && (
            <div className="error-block-container" id="error-container">
              <h1>Whoops!</h1>
              <p>Missing something? Please check the areas marked in orange before you continue.</p>
            </div>
          )}
          <div className="admiral-card mta-medical-card">
            <MTACardHeader text={'Please answer the medical declaration for the new travellers'}/>
            <div className="body">
              <Grid fluid={true}>
                {!unableToProvideQuote ? (
                  <div className="steps-container">
                    <div className="section-title">
                      <h2>Medical Declaration</h2>
                    </div>

                    <MedicalInformation/>

                    <div className="medical-section-group">

                      {this.renderQuestions()}

                      <div className="medical-condition-container" hidden={!this.shouldShowScreeningSection()}>
                        <div className="section-title">
                          <h2>Your medical condition(s)</h2>
                        </div>

                        <FieldArray
                          name="data.attributes.travellers"
                          i18n={i18n}
                          change={change}
                          component={MTATravellersMedical}
                          questions={metadata.medical_declaration_questions}
                          setIsScreening={this.setIsScreening}
                          rerenderOnEveryChange={true}
                        />
                      </div>
                    </div>
                    <WarningMessages warningMessage={screening?.errors}/>
                  </div>
                ) : (
                  <div className="steps-container unable-box">
                    <h1>Sorry, we are unable to provide a quote</h1>
                    <h4>
                      We are unable to provide any cover if you or anyone on your policy have been placed
                      on a waiting list for treatment or investigation, or been given a terminal prognosis.
                    </h4>
                  </div>
                )}
              </Grid>
            </div>
          </div>
          {!unableToProvideQuote && (
            <div>
              <div style={{ paddingTop: '35px' }}>
                <i>By clicking continue you agree to our <a
                  href="https://www.admiral.com/insurance/your-policy/conditions-of-use.php?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
                  target="_blank">conditions of use </a>
                  and <a
                    href="https://www.admiral.com/your-privacy-and-security/?&contentOnly=1&_ga=2.7793211.828839540.1508235797-2125578306.1459951786&_gac=1.217769634.1505922935.EAIaIQobChMI_rW5lpC01gIVzrztCh1dwA5aEAAYASAAEgJX9PD_BwE"
                    target="_blank">privacy statement</a>
                </i>
              </div>
              <div className="mt-30" hidden={this.state.isScreening === true}>
                <MTAButtons button1={submitButton}/>
              </div>
            </div>
          )}
        </Form>
      </>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: IMtaFormNames.COVER_DETAILS,
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector(IMtaFormNames.COVER_DETAILS);
    return component;
  },
  connect(
    (state: IStore) => {
      const travellers = selector(state, 'data.attributes.travellers');
      const metadata = selector(state, 'data.attributes.metadata');
      const medicalDeclaration = selector(state, 'data.attributes.metadata.medical_declaration_questions');
      const medicalDeclarationFromProduct = selector(state, 'medicalDeclaration');
      const salesChannel = selector(state, 'data.attributes.metadata.sales_channel');
      const selectedTravellers = selector(state, 'selectedTravellers');
      const values = selector(
        state,
        'data.attributes',
      );
      return {
        mta: state.mta,
        mtaIsLoading: state.mta.isLoading,
        mtaQuote: state.mta.mtaQuote,
        selectedPolicy: state.mta.selectedPolicy,
        branding: state.branding,
        i18n: state.branding.i18n,
        formValues: values,
        travellers,
        medicalDeclaration,
        medicalDeclarationFromProduct,
        metadata,
        salesChannel,
        selectedTravellers,
        screening: state.verisk
      };
    },
    (dispatch) => ({
      actions: {
        push: bindActionCreators(push, dispatch),
        mta: bindActionCreators({ ...mtaActions }, dispatch),
        branding: bindActionCreators({ ...brandingActions }, dispatch),
      },
    }),
  ),
])(MTAMedicalDeclarationForm);
